import { isOrbisErrorData } from '@/components/~advisor/Trading/Trading/utils';
import { appConfig } from '@oneview/config';
import { selectRequireNbbo } from '@oneview/redux/settings/settingSelectors';
import { createQueryFn } from '@oneview/redux/utils';
import {
    RebalanceRecommendationItem,
    ModelFractionalSharePolicy,
    BasketOrderValues,
    OrderTransaction,
} from '@orbis-systems/orbis-api-middleware';
import { divide, multiply, subtract, sumBy, groupBy } from 'lodash';
import numbro from 'numbro';

import {
    getTradingFormMeta,
    ModifyOrderFormValues,
    showToast,
    TradingFormValues,
} from '@orbis-systems/orbis-react-toolkit';
import { api } from './baseApi';
import { DataTags } from './dataTags';
import { prepareOrder } from '../utils/api/prepareOrder';

export const tradingApi = api.injectEndpoints({
    endpoints: (build) => ({
        previewOrder: build.mutation({
            queryFn: createQueryFn(
                async (values: TradingFormValues, { extra: api, getState }) => {
                    const requireNbbo = selectRequireNbbo(getState());
                    const meta = getTradingFormMeta(values);
                    return api.orbisMiddleware.trading.previewOrder(values, meta, requireNbbo);
                },
                {
                    pendingMessage: 'Getting order preview…',
                    alertIfError: false,
                },
            ),
        }),
        previewAlgoOrder: build.mutation({
            queryFn: createQueryFn(
                async (values, { extra: api, getState }) => {
                    const requireNbbo = selectRequireNbbo(getState());

                    const response = await api.orbis.orders.v2.equities.algoPreview({
                        requireNbbo,
                        ...values,
                    });

                    return response.data;
                },
                {
                    pendingMessage: 'Getting order preview…',
                    alertIfError: false,
                },
            ),
        }),
        placeOrder: build.mutation({
            queryFn: createQueryFn(async (values: TradingFormValues, { extra: api, dispatch, getState }) => {
                const requireNbbo = selectRequireNbbo(getState());
                const meta = getTradingFormMeta(values);
                const {
                    modelTrading,
                    inventoryTrading,
                    mutualFundTrading,
                    optionTrading,
                    spreadTrading,
                    buyWrite,
                    bondTrading,
                } = meta;

                const orderBody = prepareOrder(values, meta, requireNbbo);

                let response;

                if (bondTrading) {
                    response = await api.orbis.orders.v2.fixedIncome.bondPlacement(orderBody);
                } else if (mutualFundTrading) {
                    response = await api.orbis.orders.v2.mutualFunds.mutualFundPlacement(orderBody);
                } else if (optionTrading) {
                    if (spreadTrading || buyWrite) {
                        response = await api.orbis.orders.v2.options.placementMultileg(orderBody);
                    } else {
                        response = await api.orbis.orders.v2.options.placement(orderBody);
                    }
                } else if (modelTrading || inventoryTrading) {
                    response = await api.orbis.orders.v2.equities.advisoryPlacement(orderBody);
                } else {
                    response = await api.orbis.orders.v2.equities.placement(orderBody);
                }

                const data = {
                    orderRef: response.data.orderRef || response.data.OrderRef,
                    market: response.data.market,
                };

                dispatch(showToast('Order has been placed'));
                return data;
            }),
            invalidatesTags: [DataTags.GetAccountOrders],
        }),
        placeAlgoOrder: build.mutation({
            queryFn: createQueryFn(async (values, { extra: api, dispatch, getState }) => {
                const requireNbbo = selectRequireNbbo(getState());

                const data = await api.orbis.orders.v2.equities.algoPlacement({
                    requireNbbo,
                    ...values,
                });
                dispatch(showToast('Order has been placed'));
                return data.data;
            }),
            invalidatesTags: [DataTags.GetAccountOrders],
        }),
        previewBasketOrder: build.mutation({
            queryFn: createQueryFn(
                (
                    {
                        accountId,
                        values,
                    }: {
                        accountId: number;
                        values: BasketOrderValues[];
                    },
                    { extra: api, getState },
                ) => {
                    const requireNbbo = selectRequireNbbo(getState());
                    return api.orbisMiddleware.trading.previewBasketOrder(accountId, values, requireNbbo);
                },
                {
                    pendingMessage: 'Getting basket orders preview…',
                },
            ),
        }),
        placeBasketOrder: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        accountId,
                        values,
                    }: {
                        accountId: number;
                        values: BasketOrderValues[];
                    },
                    { extra: api, dispatch, getState },
                ) => {
                    const requireNbbo = selectRequireNbbo(getState());
                    const data = await api.orbisMiddleware.trading.placeBasketOrder(accountId, values, requireNbbo);
                    dispatch(showToast('Order has been placed'));
                    return data;
                },
            ),
            invalidatesTags: [
                DataTags.GetAccountDriftReport,
                DataTags.GetAccountOrders,
                DataTags.GetBranchAccount,
                DataTags.GetAccountPortfolio,
                DataTags.GetAccountAvailableCashForWithdrawal,
                DataTags.GetAccountBuyingPower,
            ],
        }),
        cancelOrder: build.mutation({
            queryFn: createQueryFn(
                async (
                    { orderRef, accountId, marketCode }: { orderRef: string; accountId: number; marketCode: string },
                    { extra: api, dispatch },
                ) => {
                    const response = await api.orbisMiddleware.trading.cancelOrder(orderRef, accountId, marketCode);
                    dispatch(showToast(`Order ${orderRef} has been canceled`));
                    return response;
                },
            ),
        }),
        modifyOrder: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        values,
                        accountId,
                        accountNumber,
                        marketCode,
                    }: { values: ModifyOrderFormValues; accountId: number; accountNumber: string; marketCode: string },
                    { extra: api, dispatch },
                ) => {
                    try {
                        const orderRef = await api.orbisMiddleware.trading.modifyOrder({
                            values,
                            accountId,
                            accountNumber,
                            marketCode,
                        });

                        dispatch(showToast(`Order ${orderRef} has been modified.`));
                        return orderRef;
                    } catch (error) {
                        const resError = isOrbisErrorData(error)
                            ? error?.orbisErrorMessage || error.orbisErrorCode
                            : null;
                        const errorDetails = resError ? `(${resError})` : '';

                        dispatch(showToast(`Failed to modify order ${values.orderRef} ${errorDetails}`));
                        throw error;
                    }
                },
            ),
            invalidatesTags: (_result, _error, { values }) => [{ type: DataTags.GetOrderDetails, id: values.orderRef }],
        }),
        getOrderDetails: build.query({
            queryFn: createQueryFn(
                (
                    { accountId, orderRef, market }: { accountId: number; orderRef: string; market: string },
                    { extra: api },
                ) => api.orbisMiddleware.trading.getOrderDetails(accountId, orderRef, market),
            ),
            providesTags: (_result, _error, { orderRef }) => [{ type: DataTags.GetOrderDetails, id: orderRef }],
        }),
        getTradeableCurrencies: build.query({
            queryFn: createQueryFn((_: void, { extra: api }) => api.orbisMiddleware.trading.getTradableCurrencyList()),
        }),
        getRebalanceRecommendations: build.query({
            queryFn: createQueryFn(
                (
                    {
                        modelId,
                        targets,
                        fractionalSharePolicy,
                        accountIds,
                    }: {
                        modelId: number;
                        targets: Record<string, number>;
                        fractionalSharePolicy: ModelFractionalSharePolicy;
                        accountIds?: number[];
                    },
                    { extra: api },
                ) => {
                    // copy-pasted from @orbis-systems/orbis-api-middleware/src/api/ModelApi.ts
                    async function getRebalanceRecommendations(
                        modelId: number,
                        targets: Record<string, number>,
                        accountIds: number[] = [],
                        presets: {
                            accountId: number;
                            transaction: OrderTransaction.Buy | OrderTransaction.Sell;
                            symbol: string;
                            quantity: number;
                        }[] = [],
                        fractionalSharePolicy = ModelFractionalSharePolicy.RoundDown,
                        fractionalSharePrecision = 5,
                    ): Promise<RebalanceRecommendationItem[]> {
                        const model = await api.orbisMiddleware.model.getModel(modelId);

                        if (!model) {
                            return [];
                        }

                        const results: RebalanceRecommendationItem[] = [];
                        const [driftReportItems, modelPortfolio] = await Promise.all([
                            api.orbisMiddleware.model.getDriftReport(modelId),
                            api.orbisMiddleware.model.getPortfolio(modelId, { combined: false }),
                        ]);

                        const buyingPowers = (
                            await Promise.all(
                                model.accounts.map((account) =>
                                    api.orbis.user.accountTradingLimit({ accountId: account.accountId }),
                                ),
                            )
                        ).map((item) => item.data.availableCash as number);

                        const accounts = model.accounts
                            .map<RebalanceRecommendationItem['account']>((account, index) => ({
                                ...account,
                                buyingPower: buyingPowers[index] || 0,
                                expectedBuyingPower: buyingPowers[index] || 0,
                            }))
                            .reduce<Record<string, RebalanceRecommendationItem['account']>>((result, account) => {
                                result[account.accountId] = account;
                                return result;
                            }, {});

                        const positions = groupBy(
                            modelPortfolio.entries.filter((position) => !position.short && !position.option),
                            (item) => item.symbol,
                        );

                        // Calculations
                        Object.keys(targets).forEach((symbol) => {
                            const current = driftReportItems.find((item) => item.symbol === symbol);

                            if (!current) {
                                return;
                            }

                            const quote = current.quote;
                            const expectedPrice = quote.mutualFund ? quote.navPrice : quote.lastPrice || 0;
                            const target = targets[symbol];

                            if (target === 0) {
                                positions[symbol]?.forEach((position) => {
                                    const account = accounts[position.accountId];
                                    if (account) {
                                        const positionWeight = divide(position.value || 0, account.balance);
                                        const sellValue = multiply(expectedPrice, position.quantity);

                                        results.push({
                                            account,
                                            transaction: OrderTransaction.Sell,
                                            quantity: position.quantity,
                                            currentWeight: positionWeight,
                                            targetWeight: target,
                                            error: '',
                                            expectedPrice: expectedPrice,
                                            expectedValue: sellValue,
                                            position,
                                            symbol,
                                            quote,
                                        });
                                    }
                                });
                            } else if (target) {
                                Object.values(accounts).forEach((account) => {
                                    const position = positions[symbol]?.find(
                                        (position) => position.accountId === account.accountId,
                                    );
                                    const positionWeight = divide(position?.value || 0, account.balance);

                                    if (
                                        positionWeight === target ||
                                        (accountIds.length && !accountIds.includes(account.accountId))
                                    ) {
                                        return;
                                    }

                                    const transaction =
                                        positionWeight > target ? OrderTransaction.Sell : OrderTransaction.Buy;
                                    const maxQuantity =
                                        transaction === OrderTransaction.Sell
                                            ? position?.quantity || 0
                                            : Math.max(0, divide(account.buyingPower, expectedPrice));
                                    let expectedValue = multiply(
                                        account.balance,
                                        Math.abs(subtract(positionWeight, target)),
                                    );
                                    let expectedQuantity = +numbro(
                                        Math.min(maxQuantity, divide(expectedValue, expectedPrice)),
                                    ).format({
                                        mantissa: fractionalSharePrecision,
                                        roundingFunction: Math.floor,
                                    });

                                    if (fractionalSharePolicy !== ModelFractionalSharePolicy.Fractional) {
                                        expectedQuantity =
                                            fractionalSharePolicy === ModelFractionalSharePolicy.RoundDown
                                                ? Math.floor(expectedQuantity)
                                                : Math.ceil(expectedQuantity);
                                        expectedValue = multiply(expectedQuantity, expectedPrice);
                                    }

                                    results.push({
                                        account,
                                        transaction,
                                        quantity: expectedQuantity,
                                        currentWeight: positionWeight,
                                        targetWeight: target,
                                        error: '',
                                        expectedPrice,
                                        expectedValue,
                                        position,
                                        symbol,
                                        quote,
                                    });
                                });
                            }
                        });

                        // Prevent NaN or Infinity
                        results.forEach((item) => {
                            item.quantity = isFinite(item.quantity) ? item.quantity : 0;
                            item.expectedValue = isFinite(item.expectedValue) ? item.expectedValue : 0;
                        });

                        const resultsGroupedByAccount = groupBy(results, (item) => item.account.accountId);

                        // Checking buying transactions to prevent buying more than buying power
                        Object.keys(resultsGroupedByAccount).forEach((accountId) => {
                            const account = accounts[accountId]!;
                            const buyingPower = account.buyingPower;
                            const positions = resultsGroupedByAccount[accountId]!;
                            const buyingPositions = positions.filter(
                                (item) => item.transaction === OrderTransaction.Buy,
                            );
                            const sellingPositions = positions.filter(
                                (item) => item.transaction === OrderTransaction.Sell,
                            );
                            const totalBuyingValue = sumBy(buyingPositions, (item) => item.expectedValue);
                            const totalSellingValue = sumBy(sellingPositions, (item) => item.expectedValue);

                            account.expectedBuyingPower += totalSellingValue - totalBuyingValue;

                            if (buyingPower < 0 || totalBuyingValue <= buyingPower) {
                                return;
                            }

                            const buyingValueReduceRatio = divide(buyingPower, totalBuyingValue);
                            positions
                                .filter((item) => item.transaction === OrderTransaction.Buy)
                                .forEach((item) => {
                                    const previousExpectedValue = item.expectedValue;
                                    item.expectedValue = multiply(item.expectedValue, buyingValueReduceRatio);
                                    item.quantity = +numbro(divide(item.expectedValue, item.expectedPrice)).format({
                                        mantissa: fractionalSharePrecision,
                                        roundingFunction: Math.floor,
                                    });

                                    if (fractionalSharePolicy !== ModelFractionalSharePolicy.Fractional) {
                                        item.quantity =
                                            fractionalSharePolicy === ModelFractionalSharePolicy.RoundDown
                                                ? Math.floor(item.quantity)
                                                : Math.ceil(item.quantity);
                                        item.expectedValue = multiply(item.quantity, item.expectedPrice);
                                    }

                                    // need to add back the amount that we couldn't buy
                                    account.expectedBuyingPower += subtract(previousExpectedValue, item.expectedValue);
                                });
                        });

                        presets.forEach((preset) => {
                            const recommendation = results.find(
                                (item) => item.account.accountId === preset.accountId && item.symbol === preset.symbol,
                            );
                            if (recommendation) {
                                recommendation.quantity = preset.quantity;
                                recommendation.expectedValue = multiply(preset.quantity, recommendation.expectedPrice);
                                recommendation.transaction = preset.transaction;
                            }
                        });

                        return results;
                    }

                    return getRebalanceRecommendations(
                        modelId,
                        targets,
                        accountIds,
                        [],
                        fractionalSharePolicy,
                        appConfig.fractionalSharePrecision,
                    );
                },
                { pendingMessage: 'Loading…' },
            ),
        }),
    }),
});

export const {
    usePreviewOrderMutation,
    usePlaceOrderMutation,
    usePreviewBasketOrderMutation,
    usePlaceBasketOrderMutation,
    useGetTradeableCurrenciesQuery,
    useCancelOrderMutation,
    useModifyOrderMutation,
    useGetOrderDetailsQuery,
    useGetRebalanceRecommendationsQuery,
    useLazyGetRebalanceRecommendationsQuery,
    usePreviewAlgoOrderMutation,
    usePlaceAlgoOrderMutation,
} = tradingApi;
